// Define TranslatedString locally to avoid circular dependency
export type TranslatedString = string & { __brand: "TranslatedString" };

type SubstitutionTokens<T extends string> = T extends `${infer _Prefix}__${infer Token}__${infer _Suffix}`
  ? Token | SubstitutionTokens<_Suffix> | SubstitutionTokens<_Prefix>
  : never;

type AutoTranslateArgs<T extends string> =
  SubstitutionTokens<T> extends never ? [string?] : [Record<SubstitutionTokens<T>, string>, string?];

/**
 * Translates a given text string, optionally substituting tokens with provided values.
 * It is transformed by vite-auto-translate-plugin into a traditional translation call.
 *
 * @param text - The text to be translated, which may contain substitution tokens in the form of __token__.
 * @param args - Optional arguments for substitution and context (required if there are token substitutions):
 *   - If the text contains substitution tokens, provide a Record of substitutions.
 *   - Optionally, provide a context string for additional translation context.
 * @param context - Optional argument to give translators context about this string
 *
 * @returns A TranslatedString with tokens replaced by their corresponding values.
 */
export function autoTranslate<T extends string>(text: T, ...args: AutoTranslateArgs<T>): TranslatedString {
  const substitutions = args[0] && typeof args[0] === "object" ? args[0] : undefined;

  return text.replaceAll(
    /__([a-zA-Z0-9]+)__/gm,
    (_match, p1: SubstitutionTokens<T>) => substitutions?.[p1] ?? "",
  ) as TranslatedString;
}
